import { useState } from 'react'
import { tokens } from '@/locales/translations/tokens'
import { Paperclip, X, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCreateTicketMutation, useUploadEvidenceMutation, useUpdateTicketMutation } from '../hooks/use-tickets-query'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

interface CreateTicketModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateTicketModal({
  open,
  onOpenChange,
}: CreateTicketModalProps) {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'others' as TicketType,
  })
  const [attachments, setAttachments] = useState<File[]>([])
  
  const createTicketMutation = useCreateTicketMutation()
  const uploadEvidenceMutation = useUploadEvidenceMutation()
  const updateTicketMutation = useUpdateTicketMutation()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const ticketData: CreateTicketDTO = {
        ...formData,
      }

      const createdTicket = await createTicketMutation.mutateAsync(ticketData)
      
      if (attachments.length > 0 && createdTicket?.data) {
        const ticket = Array.isArray(createdTicket.data) ? createdTicket.data[0] : createdTicket.data
        
        if (ticket?.external_id) {
          for (const file of attachments) {
            const result = await uploadEvidenceMutation.mutateAsync({
              file,
              ticketId: ticket.external_id,
            })

            await updateTicketMutation.mutateAsync({
              data: { 
                attachments: [result.attachment],
                skipEmailNotification: true
              },
              externalId: ticket.external_id,
            })
          }
        }
      }

      handleClose()
    } catch (_error) {
      // Erro tratado pelos hooks de mutation
    }
  }

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      type: 'others',
    })
    setAttachments([])
    onOpenChange(false)
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setAttachments((prev) => [...prev, ...files])
  }

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle>{t(tokens.tickets.create.modal.title)}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='space-y-6'>
          <div className='grid grid-cols-1 gap-4'>
            <div className='w-full space-y-2'>
              <Label htmlFor='title'>
                {t(tokens.tickets.create.title_label)}
              </Label>
              <Input
                id='title'
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder={t(tokens.tickets.create.title_placeholder)}
                required
                className='w-full'
              />
            </div>

            <div className='w-full space-y-2'>
              <Label htmlFor='type'>
                {t(tokens.tickets.create.type_label)}
              </Label>
              <Select
                value={formData.type}
                onValueChange={(value: TicketType) =>
                  setFormData((prev) => ({ ...prev, type: value }))
                }
              >
                <SelectTrigger className='w-full'>
                  <SelectValue
                    placeholder={t(tokens.tickets.create.type_placeholder)}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='technical_problems'>
                    {t(tokens.tickets.filter.type.technical_problems)}
                  </SelectItem>
                  <SelectItem value='integration_problems'>
                    {t(tokens.tickets.filter.type.integration_problems)}
                  </SelectItem>
                  <SelectItem value='security_issues'>
                    {t(tokens.tickets.filter.type.security_issues)}
                  </SelectItem>
                  <SelectItem value='suggestions'>
                    {t(tokens.tickets.filter.type.suggestions)}
                  </SelectItem>
                  <SelectItem value='others'>
                    {t(tokens.tickets.filter.type.others)}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='description'>
              {t(tokens.tickets.create.description_label)}
            </Label>
            <Textarea
              id='description'
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.stopPropagation()
                }
              }}
              placeholder={t(tokens.tickets.create.description_placeholder)}
              rows={8}
              required
            />
          </div>

          <div className='space-y-2'>
            <Label>{t(tokens.tickets.create.attachments_label)}</Label>
            <div className='rounded-lg border-2 border-dashed border-gray-300 p-6 text-center'>
              <Upload className='mx-auto mb-4 h-12 w-12 text-gray-400' />
              <div className='mb-4 text-sm text-gray-600'>
                {t(tokens.tickets.create.attachments_drag_text)}
              </div>
              <Button
                type='button'
                variant='outline'
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                <Paperclip className='mr-2 h-4 w-4' />
                {t(tokens.tickets.create.attachments_button)}
              </Button>
              <input
                id='file-upload'
                type='file'
                multiple
                className='hidden'
                onChange={handleFileUpload}
                accept='.pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif'
              />
            </div>

            {attachments.length > 0 && (
              <div className='space-y-2'>
                <Label>{t(tokens.tickets.create.selected_files_label)}</Label>
                <div className='space-y-2'>
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className='flex items-center justify-between rounded'
                    >
                      <div className='flex items-center gap-2'>
                        <Paperclip className='h-4 w-4 text-gray-500' />
                        <span className='text-sm'>{file.name}</span>
                        <span className='text-xs text-gray-500'>
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        onClick={() => removeAttachment(index)}
                      >
                        <X className='h-4 w-4' />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className='flex justify-end gap-3'>
            <Button type='button' variant='outline' onClick={handleClose}>
              {t(tokens.tickets.create.cancel_button)}
            </Button>
            <Button
              type='submit'
              disabled={
                createTicketMutation.isPending || !formData.title || !formData.description
              }
            >
              {createTicketMutation.isPending
                ? t(tokens.tickets.create.submitting_button)
                : t(tokens.tickets.create.submit_button)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
